import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import { DataGrid } from "@mui/x-data-grid";
import Typography from "@mui/material/Typography";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { contextObject } from "../../components/shared/ShoppingCart/ShoppingCartContext";
import { useQuery } from "@apollo/client";
import { debounce, InputAdornment, TextField } from "@mui/material";
import moment from "moment/moment";
import { useMutationCreateCart } from "../../queries/product/hooks/useMutationCreateCart";
import { NotificationType } from "../../constants/global";
import { ToasterContext } from "../../components/shared/Toaster/ToasterContext";
import { UserContext } from "../../hooks/userContext";
import { useMutationUpdateCart } from "../../queries/product/hooks/useMutationUpdateCart";
import { CART, PRODUCTS_PAGINATED } from "../../queries/product/productTypeDefs";
import { ProductDialog } from "../../components/shared/ProductDialog/ProductDialog";
import { CustomColumnMenu } from "../../components/shared/CustomColumnMenu/CustomColumnMenu";
import {
  attachStickyColumnScrollListener,
  BuyGridPagination,
  getProductListGridColumns,
  mapProductsToGridRows,
  PRODUCT_LIST_PAGE_SIZE,
  productListDataGridSx,
} from "./buyProductGrid";
import { marketplaceBuyableWhere, marketplaceBuyWhereExcludingUser } from "./productListWhere";
import { pageTitleSx } from "../../constants/designTokens";
import { BrandLoaderOverlay } from "../../components/shared/BrandLoader/BrandLoader";

export const Buy: React.FunctionComponent = () => {
  const { id }: any = useContext(UserContext);
  const { addToast } = useContext(ToasterContext);
  const { cart } = useContext(contextObject);

  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [params, setParams] = useState<any>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: PRODUCT_LIST_PAGE_SIZE,
  });

  const baseWhere = useMemo(
    () => (Number.isFinite(+id) ? marketplaceBuyWhereExcludingUser(+id) : marketplaceBuyableWhere),
    [id]
  );
  const [currentWhere, setCurrentWhere] = useState<any>(() =>
    Number.isFinite(+id) ? marketplaceBuyWhereExcludingUser(+id) : marketplaceBuyableWhere
  );

  useEffect(() => {
    setCurrentWhere(baseWhere);
  }, [baseWhere]);

  const [createCart, { loading: createCartLoading }] = useMutationCreateCart({
    refetchQueries: [
      {
        query: CART,
        variables: {
          where: { userId: { equals: +id } },
          orderBy: { createdAt: "asc" },
        },
        awaitRefetchQueries: true,
      },
    ],
  });
  const [updateCart, { loading: updateCartLoading }] = useMutationUpdateCart({
    refetchQueries: [
      {
        query: CART,
        variables: {
          where: { userId: { equals: +id } },
          orderBy: { createdAt: "asc" },
        },
        awaitRefetchQueries: true,
      },
    ],
  });
  const { data, loading: queryLoading } = useQuery(PRODUCTS_PAGINATED, {
    variables: {
      where: currentWhere,
      orderBy: { createdAt: "desc" },
      skip: paginationModel.page * paginationModel.pageSize,
      take: paginationModel.pageSize,
    },
    fetchPolicy: "no-cache",
    pollInterval: 5000,
    skip: !Number.isFinite(+id),
  });

  const paginatedResult = data?.findManyProductPaginated;
  const lastKnownCount = useRef(0);
  if (paginatedResult?.totalCount != null) {
    lastKnownCount.current = paginatedResult.totalCount;
  }
  const totalRows = lastKnownCount.current;

  const search = useMemo(
    () =>
      debounce((value: string) => {
        setPaginationModel((model) => ({ ...model, page: 0 }));
        if (value !== "") {
          setCurrentWhere({
            ...baseWhere,
            OR: [
              {
                drugName: {
                  contains: value,
                  mode: "insensitive",
                },
              },
              {
                ndc: {
                  contains: value,
                  mode: "insensitive",
                },
              },
            ],
          });
        } else {
          setCurrentWhere(baseWhere);
        }
      }, 1000),
    [baseWhere]
  );

  const handleSearch = (value: string) => {
    setSearchInput(value);
    search(value);
  };

  useEffect(() => attachStickyColumnScrollListener(), []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const q = urlParams.get("q");
    if (q) {
      setSearchInput(q);
      search(q);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [search]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const productId = urlParams.get("productId");

    if (productId && paginatedResult?.products) {
      const product = paginatedResult.products.find((p: any) => p.id === parseInt(productId));

      if (product) {
        const productRow = {
          ...product,
          expirationDate: moment(product.expDate),
          available: product.quantity,
          priceUnit: Number(product.unitPrice),
          price: Number(product.price),
          packaging: product.quantityPerPack,
          packagingValue: product.packaging,
          packType: product.type,
          sealed: product.packageCondition,
          sealDetails: product.sealDetails,
        };

        const productParams = {
          id: product.id,
          row: productRow,
        };

        setParams(productParams);
        setOpen(true);
        setSearchInput(product.drugName);
        search(product.drugName);

        const newUrl = window.location.pathname;
        window.history.replaceState({}, "", newUrl);
      }
    }
  }, [data, search]);

  const cartLoading = createCartLoading || updateCartLoading;

  const handleCart = async (params: any, packageAvailable: number = 1): Promise<void> => {
    if (cartLoading) {
      throw new Error("Cart is busy");
    }

    const requestData = {
      quantity: params.row.available,
      User: {
        connect: {
          id: +id,
        },
      },
      Product: {
        connect: {
          id: params.id,
        },
      },
    };
    const foundCartItem: any = cart.find((cartItem: any) => cartItem.item.id === params.id);
    const newCount = packageAvailable + (foundCartItem?.quantity || 0);
    if (newCount > params.row.available) {
      addToast({ type: NotificationType.Error, title: `max available count: ${params.row.available}` });
      throw new Error(`max available count: ${params.row.available}`);
    }

    try {
      if (foundCartItem) {
        await updateCart({
          variables: {
            data: { ...requestData, quantity: { increment: packageAvailable } },
            where: { id: foundCartItem.id },
          },
        });
        addToast({ type: NotificationType.Success, title: "Cart updated successfully" });
      } else {
        await createCart({ variables: { data: { ...requestData, quantity: packageAvailable } } });
      }
    } catch (error: any) {
      addToast({ type: NotificationType.Error, title: error.message || "Failed to add to cart" });
      throw error;
    }
  };

  const columns = useMemo(
    () =>
      getProductListGridColumns({
        currentUserId: +id,
        updateCartLoading: cartLoading,
        handleCart,
        onSeeDetails: (rowParams) => {
          setParams(rowParams);
          setOpen(true);
        },
      }),
    [id, cartLoading]
  );

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        flex: 1,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          bgcolor: "#FFFFFF",
          border: "1px solid #E2E8F0",
          borderRadius: "14px",
          boxShadow: "0 1px 2px rgba(15,23,42,0.06), 0 8px 24px rgba(15,23,42,0.07)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          fontFamily: '"Source Sans 3", sans-serif',
          flex: 1,
          minHeight: 0,
        }}
      >
        <>
          <Box
            sx={{
              px: 2.5,
              py: 0.75,
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid #E2E8F0",
              minHeight: 44,
            }}
          >
            <Box>
              <Typography component="h1" sx={pageTitleSx}>
                Buy
              </Typography>
            </Box>
            <TextField
              type="text"
              placeholder="Search by Drug name or NDC…"
              value={searchInput}
              onChange={(event: any) => handleSearch(event.target.value)}
              size="small"
              sx={{
                minWidth: { xs: "100%", sm: 320 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  fontFamily: '"Source Sans 3", sans-serif',
                  fontSize: 14,
                  "& fieldset": { borderColor: "#E2E8F0" },
                  "&:hover fieldset": { borderColor: "#475569" },
                  "&.Mui-focused fieldset": { borderColor: "#2563EB", borderWidth: "1.5px" },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ fontSize: 18, color: "#475569" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, width: "100%" }}>
            <DataGrid
              loading={loading || queryLoading}
              rows={mapProductsToGridRows(paginatedResult?.products)}
              columns={columns}
              rowHeight={52}
              columnHeaderHeight={40}
              autoPageSize
              slots={{
                columnMenu: CustomColumnMenu,
                pagination: BuyGridPagination,
                loadingOverlay: BrandLoaderOverlay,
              }}
              slotProps={{ pagination: { rowCount: totalRows } as any }}
              paginationMode="server"
              rowCount={totalRows}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              onRowClick={(rowParams, event: any) => {
                if (event.target.tagName === "DIV" || event.target.tagName === "SPAN") {
                  setParams(rowParams);
                  setOpen(true);
                }
              }}
              pageSizeOptions={[paginationModel.pageSize]}
              sx={{ ...productListDataGridSx, height: "100%" }}
            />
          </Box>
        </>
      </Box>
      {open && (
        <ProductDialog
          params={params}
          setParams={setParams}
          open={open}
          setOpen={setOpen}
          handleCart={handleCart}
          loading={cartLoading}
        />
      )}
    </Box>
  );
};

export default Buy;
